import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Award, GraduationCap, Stethoscope, ArrowRight, MessageCircle, BookOpen } from "lucide-react";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import NewspaperFeature from "../components/site/NewspaperFeature";
import { getPressMentionsForSubject } from "../data/press";
import { CLINIC, waLink, telLink } from "../lib/clinic";
import { REF, SITE, breadcrumb, graph } from "../lib/schema";
import { BOOK_SQUINT } from "../lib/books";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";

// Dr. Manisha's portrait — stored on the customer-assets CDN. `SafeImage`
// automatically routes it through `/api/img?url=…` so ad-blockers that block
// `customer-assets.emergentagent.com` still resolve the image via the same
// origin, and falls back to a branded initial card if the network drops.
const PORTRAIT = "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png";

const distinctions = ["Biochemistry", "Pharmacology", "Ophthalmology", "Pediatrics"];

const surgicalScope = [
  "Cataract — phacoemulsification & small-incision cataract surgery (SICS)",
  "Pediatric cataract",
  "Squint (strabismus) — all types, in children and adults",
  "Ptosis · dacryocystorhinostomy (DCR)",
  "Pterygium · entropion · ectropion",
  "SFIOL (scleral-fixated IOL)",
  "Corneoscleral laceration repair · dermoid cyst excision · enucleation",
  "Laser procedures — YAG iridotomy, capsulotomy, PRP & laser under indirect ophthalmoscopy",
];

const investigations = ["Ultrasound B-scan", "OCT", "Visual field (perimetry)", "VEP"];

const DrManishaMishra = () => {
  usePageMeta({
    title: "Dr. Manisha Mishra — Cataract, Squint & Pediatric Ophthalmologist",
    path: "/team/dr-manisha-mishra",
    description:
      "Dr. Manisha Mishra — MBBS (Gold Medalist, 4 distinctions), MS (MAMC, New Delhi), DNB, FICO (Merit). Cataract, squint & pediatric ophthalmology consultant at Shantanu Netralaya Eye Hospital, Varanasi.",
  });

  const jsonLd = useMemo(() => {
    const pageUrl = `${SITE}/team/dr-manisha-mishra`;
    return graph([
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Our Team", path: "/team/dr-manisha-mishra" },
        { name: "Dr. Manisha Mishra", path: "/team/dr-manisha-mishra" },
      ]),
      {
        "@type": "ProfilePage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Dr. Manisha Mishra — Consultant Ophthalmologist",
        mainEntity: { "@id": REF.manishaPhysician || "https://drshantanugupta.com/#physician-manisha" },
        isPartOf: { "@id": REF.website },
        primaryImageOfPage: { "@type": "ImageObject", url: PORTRAIT },
      },
      {
        "@type": ["Physician", "Person"],
        "@id": "https://drshantanugupta.com/#physician-manisha",
        name: "Dr. Manisha Mishra",
        givenName: "Manisha",
        familyName: "Mishra",
        honorificPrefix: "Dr.",
        jobTitle: "Consultant Ophthalmologist — Cataract, Squint & Pediatric Ophthalmology",
        image: PORTRAIT,
        url: pageUrl,
        telephone: "+91-8318665154",
        worksFor: { "@id": REF.clinic },
        affiliation: { "@id": REF.clinic },
        address: {
          "@type": "PostalAddress",
          streetAddress: "40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund",
          addressLocality: "Varanasi",
          addressRegion: "Uttar Pradesh",
          postalCode: "221005",
          addressCountry: "IN",
        },
        medicalSpecialty: ["Ophthalmic", "Ophthalmology", "PediatricOphthalmology", "Strabismus"],
        // Verified off-domain profiles that identify the same real-world
        // person. This `sameAs` graph is what search engines use to unify
        // the Person entity across the web (Practo listing + LinkedIn +
        // published book on Amazon).
        sameAs: [
          "https://in.linkedin.com/in/dr-manisha-mishra-142623159",
          "https://www.practo.com/varanasi/doctor/manisha-mishra-ophthalmologist",
          "https://www.eyedocmanishamishra.com",
          BOOK_SQUINT.amazonUrl,
        ],
        knowsAbout: [
          "Cataract Surgery",
          "Phacoemulsification",
          "Small-Incision Cataract Surgery",
          "Pediatric Cataract",
          "Squint Surgery",
          "Strabismus",
          "Pediatric Ophthalmology",
          "Ptosis Surgery",
          "Dacryocystorhinostomy",
          "SFIOL",
          "Amblyopia",
          "ROP Screening",
        ],
        // Published works — Book schema linked via `author`. Boosts E-E-A-T.
        author: [
          {
            "@type": "Book",
            "@id": `${SITE}/book/${BOOK_SQUINT.slug}#book`,
            name: BOOK_SQUINT.fullTitle,
            isbn: BOOK_SQUINT.isbn13,
            datePublished: BOOK_SQUINT.publicationDate,
            inLanguage: "en",
            bookFormat: "https://schema.org/Paperback",
            publisher: { "@type": "Organization", name: BOOK_SQUINT.publisher },
            url: `${SITE}/book/${BOOK_SQUINT.slug}`,
            sameAs: [BOOK_SQUINT.amazonUrl],
          },
        ],
        alumniOf: [
          { "@type": "CollegeOrUniversity", name: "Mahatma Gandhi Medical College, Indore" },
          { "@type": "CollegeOrUniversity", name: "Maulana Azad Medical College (MAMC), New Delhi" },
          { "@type": "CollegeOrUniversity", name: "Guru Nanak Eye Centre, MAMC, New Delhi" },
        ],
        hasCredential: [
          { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "MBBS (Gold Medalist — 4 distinctions)" },
          { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "MS Ophthalmology (MAMC, New Delhi)" },
          { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "DNB Ophthalmology" },
          { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "FICO (International Council of Ophthalmology) — Merit" },
          { "@type": "EducationalOccupationalCredential", credentialCategory: "publication", name: `Author of "${BOOK_SQUINT.fullTitle}" (${BOOK_SQUINT.publisher}, 2026)` },
        ],
        award: [
          "Best Poster Award — Delhi Ophthalmological Society (DOS) 2016",
          "AIR 576 — All India Pre-Medical Test (first attempt)",
          "AIR 419 — NEET PG (first attempt)",
          "MBBS Gold Medals — Biochemistry, Pharmacology, Ophthalmology, Pediatrics",
        ],
        areaServed: [
          { "@type": "City", name: "Varanasi" },
          { "@type": "State", name: "Uttar Pradesh" },
        ],
        // Real-world print press coverage — passes E-E-A-T authority to
        // Google.  Each mention becomes a NewsArticle node linked back
        // to this Physician via `about`.
        subjectOf: [
          {
            "@type": "NewsArticle",
            "@id": "https://drshantanugupta.com/press#dainik-jagran-purvanchal-25-jul-2026",
            headline:
              "डॉ. मनीषा मिश्रा की पहल से पूर्वांचल में आधुनिक स्क्विंट सर्जरी हुई अधिक सुलभ",
            alternativeHeadline:
              "Dr. Manisha Mishra's initiative makes modern squint surgery more accessible across Purvanchal",
            inLanguage: "hi-IN",
            datePublished: "2026-07-25",
            about: { "@id": "https://drshantanugupta.com/#physician-manisha" },
            publisher: {
              "@type": "NewsMediaOrganization",
              name: "Dainik Jagran",
              url: "https://www.jagran.com/",
            },
            url: "https://epaper.jagran.com/epaper/25-jul-2026-45-varanasi-city-edition-varanasi-city.html",
            isBasedOn:
              "https://epaper.jagran.com/epaper/25-jul-2026-45-varanasi-city-edition-varanasi-city.html",
            printEdition: "Purvanchal Jagran — Varanasi City Edition",
            spatialCoverage: [
              "Varanasi", "Chandauli", "Ghazipur", "Jaunpur",
              "Bhadohi (Sant Ravidas Nagar)", "Mirzapur", "Sonbhadra",
              "Azamgarh", "Mau", "Ballia",
            ].map((n) => ({ "@type": "AdministrativeArea", name: n })),
          },
        ],
      },
    ]);
  }, []);

  usePageJsonLd(jsonLd);

  return (
    <div data-testid="dr-manisha-page">
      {/* HERO */}
      <section className="pt-20 pb-8 container-page">
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            <li><Link to="/" className="hover:text-[hsl(var(--primary))]">Home</Link></li>
            <li>›</li>
            <li className="text-[hsl(var(--foreground))]/80">Dr. Manisha Mishra</li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Consultant · Shantanu Netralaya</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance" data-testid="dr-manisha-h1">
              Dr. Manisha Mishra
            </h1>
            <p className="mt-4 text-lg text-[hsl(var(--primary))] font-serif">
              MBBS (Gold Medalist) · MS (MAMC, New Delhi) · DNB · FICO (Merit)
            </p>
            <Link
              to={`/book/${BOOK_SQUINT.slug}`}
              className="mt-4 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] transition"
              data-testid="manisha-book-credential"
            >
              <BookOpen size={13} strokeWidth={1.8} />
              Published Author · <span className="normal-case tracking-normal font-serif italic">"{BOOK_SQUINT.title}"</span>
              <ArrowRight size={12} />
            </Link>
            <div className="mt-4 flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-[0.18em]" data-testid="manisha-cross-domain">
              <span className="text-muted-foreground">Also on the web:</span>
              <a
                href="https://www.eyedocmanishamishra.com"
                target="_blank"
                rel="me noopener noreferrer"
                className="text-[hsl(var(--primary))] hover:underline underline-offset-2"
              >
                eyedocmanishamishra.com ↗
              </a>
              <span className="text-muted-foreground">·</span>
              <a
                href="https://www.shantanunetralaya.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--primary))] hover:underline underline-offset-2"
              >
                shantanunetralaya.com ↗
              </a>
            </div>
            <p className="mt-6 max-w-2xl prose-elegant">
              Cataract, squint (strabismus) and pediatric ophthalmology consultant at Shantanu Netralaya Eye Hospital, Varanasi. MAMC-trained; senior residency at Guru Nanak Eye Centre under one of India's pioneers in strabismus surgery.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink("Hello, I would like to book a consultation with Dr. Manisha Mishra at Shantanu Netralaya, Varanasi.")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="manisha-cta-whatsapp"
              >
                <MessageCircle size={16} /> Book a consultation
              </a>
              <a href={telLink()} className="btn-ghost" data-testid="manisha-cta-call">
                Call {CLINIC.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-[2rem] border border-border/70 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.25)] aspect-[4/5]">
              <SafeImage
                src={PORTRAIT}
                alt="Dr. Manisha Mishra — Cataract, Squint & Pediatric Ophthalmology Consultant at Shantanu Netralaya Eye Hospital, Durgakund, Varanasi"
                label="Dr. Manisha Mishra"
                loading="eager"
                className="w-full h-full"
                data-testid="dr-manisha-portrait"
              />
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-10 container-page max-w-3xl" data-testid="manisha-journey">
        <SectionHeader overline="A path shaped by academics" title="Journey into ophthalmology" />
        <div className="prose-elegant mt-6 space-y-4">
          <p>
            Dr. Manisha entered medicine in <strong>2008</strong> with an <strong>all-India rank of 576 (AIR 576)</strong> in her very first attempt. She pursued MBBS at <strong>Mahatma Gandhi Medical College, Indore</strong> — one of the oldest medical colleges in India, and among the most prestigious in Central India.
          </p>
          <p>
            Consistently academically inclined, she earned <strong>four gold medals</strong> during MBBS — in {distinctions.join(", ")}.
          </p>
          <p>
            After clearing NEET PG with an <strong>all-India rank of 419 (AIR 419)</strong> in her first attempt, she chose ophthalmology at <strong>Maulana Azad Medical College (MAMC), New Delhi</strong> — one of the country's most prestigious ophthalmology programmes.
          </p>
          <p>
            She won the <strong>Best Poster Award at DOS 2016</strong> for presenting her thesis. After completing MS in 2017, she joined <strong>Guru Nanak Eye Centre, MAMC</strong> as a Senior Resident, training under one of India's <em>pioneers in strabismus surgery</em> while gaining hands-on training in cataract surgery, pediatric ophthalmology, lid surgery and dacryocystorhinostomy.
          </p>
          <p>
            During her senior residency, she also cleared the <strong>DNB</strong> examination and the <strong>FICO</strong> (Fellowship of the International Council of Ophthalmology) — the latter with <em>merit</em>.
          </p>
        </div>
      </section>

      {/* CREDENTIALS GRID */}
      <section className="py-10 container-page" data-testid="manisha-credentials">
        <SectionHeader overline="Training & credentials" title="A rare depth of academic rigour" />
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <div className="p-6 rounded-2xl border border-border bg-card/70 dark:bg-white/[0.03]">
            <GraduationCap size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" />
            <h3 className="mt-3 font-serif text-xl">Alma mater</h3>
            <ul className="mt-3 text-sm space-y-1.5 text-[hsl(var(--foreground))]/85">
              <li>Mahatma Gandhi Medical College, Indore — MBBS</li>
              <li>Maulana Azad Medical College, New Delhi — MS Ophthalmology</li>
              <li>Guru Nanak Eye Centre, MAMC — Senior Residency</li>
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-border bg-card/70 dark:bg-white/[0.03]">
            <Award size={22} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
            <h3 className="mt-3 font-serif text-xl">Distinctions</h3>
            <ul className="mt-3 text-sm space-y-1.5 text-[hsl(var(--foreground))]/85">
              <li>AIR 576 · All India PMT (first attempt)</li>
              <li>AIR 419 · NEET PG (first attempt)</li>
              <li>4 Gold Medals in MBBS ({distinctions.join(", ")})</li>
              <li>Best Poster Award · DOS 2016</li>
              <li>FICO — Merit</li>
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-border bg-card/70 dark:bg-white/[0.03]">
            <Stethoscope size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" />
            <h3 className="mt-3 font-serif text-xl">Investigations trained in</h3>
            <ul className="mt-3 text-sm space-y-1.5 text-[hsl(var(--foreground))]/85">
              {investigations.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* PUBLISHED AUTHOR — book showcase. Between credentials and
          surgical scope, this positions the book as an authoritative
          artifact that legitimises the surgical scope that follows. */}
      <section className="py-12 container-page" data-testid="manisha-book-section">
        <div className="rounded-3xl border border-border bg-[hsl(var(--secondary))]/40 p-8 sm:p-10 grid md:grid-cols-[minmax(0,0.55fr)_minmax(0,1fr)] gap-8 items-center max-w-4xl mx-auto">
          <Link
            to={`/book/${BOOK_SQUINT.slug}`}
            className="block max-w-[240px] mx-auto md:mx-0 group"
            data-testid="manisha-book-cover-link"
          >
            <div className="relative overflow-hidden rounded-2xl border border-border shadow-[0_25px_60px_-25px_rgba(0,0,0,0.35)] group-hover:shadow-[0_35px_80px_-25px_rgba(0,0,0,0.45)] transition-shadow aspect-[2/3]">
              <SafeImage
                src={BOOK_SQUINT.coverImageThumb}
                alt={`${BOOK_SQUINT.fullTitle} — book cover`}
                label={BOOK_SQUINT.title}
                className="w-full h-full"
              />
            </div>
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">Published Author</div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl leading-snug">
              {BOOK_SQUINT.title}: <span className="text-[hsl(var(--primary))]">{BOOK_SQUINT.subtitle}</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {BOOK_SQUINT.publisher} · {BOOK_SQUINT.pages} pages · ISBN {BOOK_SQUINT.isbn13}
            </p>
            <p className="mt-4 text-[15px] text-[hsl(var(--foreground))]/85">
              Dr. Manisha's patient-facing guide to squint (strabismus) — an accessible book that helps families understand diagnosis, treatment options, and what to expect from squint surgery. Edited for medical accuracy by Dr. Shantanu Kumar Gupta.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/book/${BOOK_SQUINT.slug}`} className="btn-primary" data-testid="manisha-book-details-btn">
                Read about the book
              </Link>
              <a
                href={BOOK_SQUINT.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="manisha-book-amazon-btn"
              >
                View on Amazon ↗
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SURGICAL SCOPE */}
      <section className="py-10 container-page max-w-3xl" data-testid="manisha-scope">
        <SectionHeader overline="Surgical scope" title="Procedures she performs" />
        <p className="mt-6 prose-elegant">
          Dr. Manisha has performed countless procedures over her training and consultancy years, ranging from routine to complex — covering:
        </p>
        <ul className="mt-5 space-y-2.5">
          {surgicalScope.map((s, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
              <span className="text-[15px]">{s}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* SERVICES CROSS-LINKS */}
      <section className="py-10 container-page" data-testid="manisha-services">
        <SectionHeader overline="Consult Dr. Manisha for" title="Areas of expertise" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {[
            { slug: "complex-cataract-surgery-in-varanasi", title: "Complex & Complicated Cataract Surgery" },
            { slug: "squint-surgery-in-varanasi", title: "Squint (Strabismus) Surgery in Varanasi" },
            { slug: "pediatric-ophthalmologist-in-varanasi", title: "Pediatric Ophthalmologist in Varanasi" },
          ].map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`manisha-service-${s.slug}`}
              className="group border border-border rounded-xl p-6 hover:border-[hsl(var(--primary))] transition-colors"
            >
              <div className="font-serif text-lg leading-snug group-hover:text-[hsl(var(--primary))]">
                {s.title}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition">
                Learn more <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PRESS COVERAGE — real print mentions of Dr. Manisha's squint
          practice. Renders nothing if no mention with subject.pageHref
          matches this page. Placed just before the closing CTA so the
          highest-authority signal is the last thing users see. */}
      {(() => {
        const mentions = getPressMentionsForSubject("/team/dr-manisha-mishra");
        if (mentions.length === 0) return null;
        return (
          <section className="py-14 container-page" data-testid="manisha-press-section">
            <SectionHeader overline="As featured in the press" title="Print coverage across Purvanchal" />
            <p className="text-sm text-muted-foreground max-w-2xl mt-3 mb-8">
              Dr. Manisha Mishra's squint surgery initiative has been recognised by regional print media reaching every district of eastern Uttar Pradesh.
            </p>
            <div className="space-y-8">
              {mentions.map((m) => (
                <NewspaperFeature key={m.id} mention={m} variant="full" />
              ))}
            </div>
          </section>
        );
      })()}

      {/* CTA */}
      <section className="py-14 container-page" data-testid="manisha-cta">
        <div className="rounded-3xl border border-border bg-[hsl(var(--muted))]/40 p-10 text-center">
          <div className="overline mb-3">Book a consultation</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
            Meet Dr. Manisha at Shantanu Netralaya
          </h2>
          <p className="mt-3 max-w-lg mx-auto text-sm text-[hsl(var(--foreground))]/75">
            {CLINIC.address}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={waLink("Hello, I would like to book a consultation with Dr. Manisha Mishra at Shantanu Netralaya, Varanasi.")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="manisha-cta-bottom-whatsapp"
            >
              <MessageCircle size={16} /> Book on WhatsApp
            </a>
            <a href={telLink()} className="btn-ghost" data-testid="manisha-cta-bottom-call">
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DrManishaMishra;
